import React from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Alert,
  Button,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import TargetIcon from '@mui/icons-material/GpsFixed';
import ImageIcon from '@mui/icons-material/Image';

const LaunchSummary = ({ campaignData, onLaunch, loading }) => {
  // Calculate total budget
  const dailyBudget = campaignData.adSet.budget || 0;
  const numberOfAccounts = campaignData.selectedAccounts?.length || 0;
  const totalDailyBudget = dailyBudget * numberOfAccounts;
  const numberOfAds = campaignData.ads.length;

  // Check for any warnings
  const warnings = [];
  if (campaignData.campaign.status === 'ACTIVE') {
    warnings.push('Campaign will start running immediately after creation');
  }
  if (dailyBudget > 100) {
    warnings.push('High daily budget detected');
  }
  if (numberOfAccounts > 1 && totalDailyBudget > 100) {
    warnings.push(`Total daily budget across ${numberOfAccounts} accounts will be $${totalDailyBudget.toFixed(2)}`);
  }
  if (numberOfAds > 5) {
    warnings.push('Large number of ad variations');
  }
  if (numberOfAccounts > 1) {
    warnings.push(`This will create ${numberOfAccounts} identical campaigns, one for each selected account`);
  }

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Campaign Overview */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Campaign Overview
            </Typography>
            <List>
              <ListItem>
                <ListItemText
                  primary="Facebook Accounts"
                  secondary={
                    campaignData.selectedAccounts && campaignData.selectedAccounts.length > 0 
                      ? campaignData.selectedAccounts.map(acc => `${acc.name} (${acc.id})`).join(', ')
                      : 'Not selected'
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Campaign Name"
                  secondary={campaignData.campaign.name}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Objective"
                  secondary={campaignData.campaign.objective}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Status"
                  secondary={
                    <Chip
                      label={campaignData.campaign.status}
                      color={campaignData.campaign.status === 'ACTIVE' ? 'success' : 'warning'}
                      size="small"
                    />
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Content Page"
                  secondary={campaignData.selectedPage.title}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Facebook Page ID"
                  secondary={campaignData.campaign.facebookPageId || 'Not selected'}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Special Ad Categories"
                  secondary={
                    campaignData.campaign.specialAdCategories && campaignData.campaign.specialAdCategories.length > 0
                      ? (
                        <Box sx={{ mt: 1 }}>
                          {campaignData.campaign.specialAdCategories.map((category) => (
                            <Chip
                              key={category}
                              label={
                                category === 'FINANCIAL_PRODUCTS_SERVICES' ? 'Financial Products & Services' :
                                category === 'EMPLOYMENT' ? 'Employment' :
                                category === 'HOUSING' ? 'Housing' :
                                category === 'ISSUES_ELECTIONS_POLITICS' ? 'Social Issues/Politics' :
                                category
                              }
                              size="small"
                              color="warning"
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          ))}
                        </Box>
                      )
                      : (
                        <Chip
                          label="None - Standard targeting"
                          size="small"
                          color="success"
                        />
                      )
                  }
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Ad Set Details */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Ad Set Details
            </Typography>
            <List>
              <ListItem>
                <ListItemText
                  primary="Ad Set Name"
                  secondary={campaignData.adSet.name}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Daily Budget"
                  secondary={`$${campaignData.adSet.budget.toFixed(2)}`}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Optimization Goal"
                  secondary={campaignData.adSet.optimization_goal}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Billing Event"
                  secondary={campaignData.adSet.billing_event}
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Targeting Summary */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TargetIcon sx={{ mr: 1 }} />
              <Typography variant="h6">
                Targeting
              </Typography>
            </Box>
            <List>
              <ListItem>
                <ListItemText
                  primary="Countries"
                  secondary={
                    <Box sx={{ mt: 1 }}>
                      {campaignData.adSet.targeting.countries.map((country) => (
                        <Chip
                          key={country}
                          label={country}
                          size="small"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                    </Box>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Interests"
                  secondary={
                    <Box sx={{ mt: 1 }}>
                      {campaignData.adSet.targeting.interests.map((interest) => (
                        <Chip
                          key={interest}
                          label={interest}
                          size="small"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                    </Box>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Devices"
                  secondary={
                    <Box sx={{ mt: 1 }}>
                      <Chip
                        label="Mobile Only"
                        size="small"
                        color="primary"
                        sx={{ mr: 0.5, mb: 0.5 }}
                      />
                    </Box>
                  }
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Ad Variations */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <ImageIcon sx={{ mr: 1 }} />
              <Typography variant="h6">
                Ad Variations
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {numberOfAds} ad variation{numberOfAds !== 1 ? 's' : ''} created
            </Typography>
            <List>
              {campaignData.ads.map((ad, index) => (
                <React.Fragment key={ad.variationId}>
                  <ListItem>
                    <ListItemText
                      primary={`Variation ${index + 1}`}
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" component="div">
                            {ad.copy.headline}
                          </Typography>
                          {!ad.validation.isValid && (
                            <Chip
                              icon={<WarningIcon />}
                              label="Has validation issues"
                              color="warning"
                              size="small"
                              sx={{ mt: 1 }}
                            />
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < campaignData.ads.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Budget Summary */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <MonetizationOnIcon sx={{ mr: 1 }} />
              <Typography variant="h6">
                Budget Summary
              </Typography>
            </Box>
            {numberOfAccounts > 1 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Creating campaigns for {numberOfAccounts} accounts. Budget shown is total across all accounts.
              </Alert>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" color="primary">
                    ${totalDailyBudget.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Daily Budget {numberOfAccounts > 1 ? `(${numberOfAccounts} accounts)` : ''}
                  </Typography>
                  {numberOfAccounts > 1 && (
                    <Typography variant="caption" color="text.secondary">
                      ${dailyBudget.toFixed(2)} per account
                    </Typography>
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" color="primary">
                    ${(totalDailyBudget * 7).toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Weekly Estimate
                  </Typography>
                  {numberOfAccounts > 1 && (
                    <Typography variant="caption" color="text.secondary">
                      ${(dailyBudget * 7).toFixed(2)} per account
                    </Typography>
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" color="primary">
                    ${(totalDailyBudget * 30).toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Monthly Estimate
                  </Typography>
                  {numberOfAccounts > 1 && (
                    <Typography variant="caption" color="text.secondary">
                      ${(dailyBudget * 30).toFixed(2)} per account
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Warnings and Launch Button */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            {warnings.length > 0 && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Please review the following:
                </Typography>
                <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                  {warnings.map((warning, index) => (
                    <li key={index}>{warning}</li>
                  ))}
                </ul>
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={onLaunch}
                disabled={loading}
                startIcon={<CheckCircleIcon />}
                sx={{ minWidth: 200 }}
              >
                {loading ? 'Launching...' : numberOfAccounts > 1 ? `Launch ${numberOfAccounts} Campaigns` : 'Launch Campaign'}
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default LaunchSummary; 